import React, { useState, useEffect } from 'react';
import { Icon } from './Icon';
import { useDebounce } from '../hooks/useDebounce';

interface DealsSearchFiltersProps {
  onFiltersChange: (filters: DealFilters) => void;
  onSearch: () => void;
  loading?: boolean;
}

export interface DealFilters {
  searchTerm: string;
  agentId: string;
  sourceId: string;
  statusId: string;
}

// Mock data - En un proyecto real, estos datos vendrían de la API
const mockAgents = [
  { id: '3', name: 'Agente 1' },
  { id: '4', name: 'Agente 2' },
  { id: '10', name: 'Esteban Valenzuela' },
];

const mockSources = [
  { id: '1', name: 'Facebook' },
  { id: '2', name: 'Whatsapp' },
];


const mockStatuses = [
  { id: '1', name: 'POSTULADO' },
  { id: '2', name: 'E1' },
  { id: '3', name: 'E2' },
  { id: '4', name: 'E3' },
  { id: '5', name: 'ADMISION EFECTIVA' },
  { id: '6', name: 'NO ADMITIDO' },
  { id: '7', name: 'MATRICULADO' },
];

export const DealsSearchFilters: React.FC<DealsSearchFiltersProps> = ({
  onFiltersChange,
  onSearch,
  loading = false
}) => {
  const [filters, setFilters] = useState<DealFilters>({
    searchTerm: '',
    agentId: '',
    sourceId: '',
    statusId: ''
  });

  const [showFilters, setShowFilters] = useState(false);
  const debouncedSearchTerm = useDebounce(filters.searchTerm, 300);

  // Notificar cambios en los filtros
  useEffect(() => {
    onFiltersChange(filters);
  }, [filters, onFiltersChange]);

  // Auto-buscar cuando cambia el término de búsqueda (con debounce)
  useEffect(() => {
    if (debouncedSearchTerm !== filters.searchTerm) {
      onSearch();
    }
  }, [debouncedSearchTerm, filters.searchTerm, onSearch]);

  const handleFilterChange = (key: keyof DealFilters, value: string) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSearch = () => {
    onSearch();
  };

  const clearFilters = () => {
    setFilters({
      searchTerm: '',
      agentId: '',
      sourceId: '',
      statusId: ''
    });
    setShowFilters(false);
    onSearch();
  };

  const hasActiveFilters = filters.agentId || filters.sourceId || filters.statusId;

  return (
    <div className="card p-6">
      {/* Barra de búsqueda principal */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
        <div className="flex-1 max-w-md">
          <div className="relative">
            <Icon name="search" className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Buscar por nombre, email o teléfono..."
              value={filters.searchTerm}
              onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            />
          </div>
        </div>

        <div className="flex items-center space-x-3">
          {/* Botón de filtros */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              showFilters || hasActiveFilters
                ? 'bg-blue-100 text-blue-700 hover:bg-blue-200 dark:bg-blue-900 dark:text-blue-300 dark:hover:bg-blue-800'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
            }`}
          >
            <Icon name="filter" className="w-4 h-4 mr-2" />
            Filtros
            {hasActiveFilters && (
              <span className="ml-2 px-2 py-1 text-xs bg-blue-500 text-white rounded-full">
                {[filters.agentId, filters.sourceId, filters.statusId].filter(Boolean).length}
              </span>
            )}
          </button>

          {/* Botón de búsqueda */}
          <button
            onClick={handleSearch}
            disabled={loading}
            className="flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 rounded-lg transition-colors"
          >
            {loading ? (
              <Icon name="loading" className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Icon name="search" className="w-4 h-4 mr-2" />
            )}
            Buscar
          </button>

          {/* Botón limpiar filtros */}
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="flex items-center px-3 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
            >
              <Icon name="x" className="w-4 h-4 mr-1" />
              Limpiar
            </button>
          )}
        </div>
      </div>

      {/* Panel de filtros expandible */}
      {showFilters && (
        <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Filtro por Agente */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Agente Asignado
              </label>
              <div className="relative">
                <Icon name="chevron-down" className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                <select
                  value={filters.agentId}
                  onChange={(e) => handleFilterChange('agentId', e.target.value)}
                  className="w-full pl-4 pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white appearance-none"
                >
                  <option value="">Todos los agentes</option>
                  {mockAgents.map((agent) => (
                    <option key={agent.id} value={agent.id}>
                      {agent.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Filtro por Fuente */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Fuente
              </label>
              <div className="relative">
                <Icon name="chevron-down" className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                <select
                  value={filters.sourceId}
                  onChange={(e) => handleFilterChange('sourceId', e.target.value)}
                  className="w-full pl-4 pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white appearance-none"
                >
                  <option value="">Todas las fuentes</option>
                  {mockSources.map((source) => (
                    <option key={source.id} value={source.id}>
                      {source.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Filtro por Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Estado
              </label>
              <div className="relative">
                <Icon name="chevron-down" className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                <select
                  value={filters.statusId}
                  onChange={(e) => handleFilterChange('statusId', e.target.value)}
                  className="w-full pl-4 pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white appearance-none"
                >
                  <option value="">Todos los estados</option>
                  {mockStatuses.map((status) => (
                    <option key={status.id} value={status.id}>
                      {status.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Resumen de filtros activos */}
          {hasActiveFilters && (
            <div className="mt-4 flex flex-wrap gap-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">Filtros activos:</span>
              {filters.agentId && (
                <span className="inline-flex items-center px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full dark:bg-blue-900 dark:text-blue-300">
                  Agente: {mockAgents.find(a => a.id === filters.agentId)?.name}
                  <button
                    onClick={() => handleFilterChange('agentId', '')}
                    className="ml-1 hover:text-blue-600"
                  >
                    <Icon name="x" className="w-3 h-3" />
                  </button>
                </span>
              )}
              {filters.sourceId && (
                <span className="inline-flex items-center px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full dark:bg-green-900 dark:text-green-300">
                  Fuente: {mockSources.find(s => s.id === filters.sourceId)?.name}
                  <button
                    onClick={() => handleFilterChange('sourceId', '')}
                    className="ml-1 hover:text-green-600"
                  >
                    <Icon name="x" className="w-3 h-3" />
                  </button>
                </span>
              )}
              {filters.statusId && (
                <span className="inline-flex items-center px-2 py-1 text-xs bg-purple-100 text-purple-800 rounded-full dark:bg-purple-900 dark:text-purple-300">
                  Estado: {mockStatuses.find(s => s.id === filters.statusId)?.name}
                  <button
                    onClick={() => handleFilterChange('statusId', '')}
                    className="ml-1 hover:text-purple-600"
                  >
                    <Icon name="x" className="w-3 h-3" />
                  </button>
                </span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
